/**
 * กลไกอัปโหลด/ดาวน์โหลดไฟล์แบบผ่านการยืนยันตัวตน — โมดูลแรกของกลุ่มนี้ในแอปทั้งก้อน
 * (ก่อน `WO-P2-M11-006` ไม่มี `FormData` และไม่มี `createObjectURL` อยู่ที่ไหนเลยใน
 * `apps/web`) แยกออกจาก `desktop/sales/SalesOrderScreen.tsx` โดยตั้งใจ: หน้าจอนำเข้า
 * ถัดไป (initial stock, forecast — ยังไม่ถูกเขียน) จะเรียกซ้ำสองฟังก์ชันนี้ ไม่ใช่
 * เขียน FormData/blob ของตัวเองอีกรอบ
 *
 * **อัปโหลด: ส่งไบต์ดิบเป็น request body ตรง ๆ ไม่ใช่ multipart `FormData`** —
 * `POST /v1/sales-orders/import-spreadsheet` อ่าน body จาก `request.stream()` ตรง ๆ
 * (`apps/api/src/cap_erp_api/modules/sales/spreadsheet.py`, ยืนยันซ้ำใน
 * `apps/api/tests/sales_order_api_support.py::upload_spreadsheet`: "the body is the
 * file itself — not a multipart form"). การห่อไฟล์ด้วย `FormData` จะแปะ boundary ของ
 * multipart ไว้หน้า/หลังไบต์ของไฟล์ แล้วปลายทางที่พยายามเปิดสิ่งที่ได้รับเป็น `.xlsx`
 * ตรง ๆ จะได้ `422 SPREADSHEET_IS_NOT_A_WORKBOOK` เสมอไม่ว่าไฟล์จะถูกต้องแค่ไหน —
 * `uploadFile()` จึงส่ง `Blob`/`File` เป็น body ตรง ๆ พร้อม header `Content-Type` ที่
 * ผู้เรียกกำหนด
 *
 * **ดาวน์โหลด: ผ่าน `fetch` ที่แนบ token เดียวกับทุก client ในแอป ไม่ใช่ `<a href>`
 * ดิบ** — เส้นทางที่ gate ด้วยสิทธิ์ (เช่น `SALES` + VIEW ของฟอร์ม Excel) ต้องพา
 * `Authorization: Bearer` ไปด้วย ซึ่งการนำทางเบราว์เซอร์ตรง ๆ ไม่มีทางแนบให้ได้เลย —
 * ผู้ใช้จะเห็น `401` เรนเดอร์เป็นดาวน์โหลดที่พังโดยไม่มีข้อความ บล็อบที่ได้มาจึงถูก
 * แปลงเป็น object URL **ชั่วครู่เดียว** เพื่อสั่งเซฟ แล้วถูก `revokeObjectURL` ทันทีใน
 * ฟังก์ชันเดียวกัน — ผู้เรียกไม่มีบล็อบให้ถือไว้เอง จึงไม่มีทางเรียกแล้วลืม revoke
 */

import { apiFetch } from '../auth/authorizedFetch';

export interface UploadFileOptions {
  /** inject ได้เพื่อการทดสอบ — ค่าเริ่มต้นคือ `apiFetch` ตัวเดียวกับ client อื่นทุกตัว */
  readonly fetchImpl?: typeof fetch;
  readonly signal?: AbortSignal;
}

/**
 * ผลของการอัปโหลด **ระดับเครือข่ายเท่านั้น** — การแปล status code เป็นความล้มเหลว
 * ทางธุรกิจเป็นของผู้เรียก (เช่น `lib/sales/client.ts`) เพราะกฎนั้นเป็นของแต่ละ
 * contract ไม่ใช่ของกลไกอัปโหลดที่ใช้ซ้ำได้
 */
export type UploadFileResult =
  | { readonly status: 'ready'; readonly response: Response }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'cancelled' };

/**
 * ส่ง `body` เป็น request body ตรง ๆ ผ่าน `fetch` ที่ยืนยันตัวตนแล้ว
 *
 * `contentType` เป็นพารามิเตอร์แยกจาก `body.type` โดยตั้งใจ: `File.type` ที่
 * เบราว์เซอร์เดามาจากนามสกุลไฟล์ไม่เสมอตรงกับ media type ที่ปลายทางต้องการเป๊ะ ๆ
 * (บางเบราว์เซอร์ไม่รู้จัก
 * `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet` และคืน `""`)
 * ผู้เรียกจึงเป็นผู้กำหนด header ที่ส่งจริงเสมอ ไม่ใช่ปล่อยให้เบราว์เซอร์เดา
 */
export async function uploadFile(
  url: string,
  body: Blob,
  contentType: string,
  options: UploadFileOptions = {},
): Promise<UploadFileResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  let response: Response;
  try {
    response = await fetchImpl(url, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Content-Type': contentType, Accept: 'application/json' },
      body,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }
  return { status: 'ready', response };
}

export interface DownloadFileOptions {
  /** inject ได้เพื่อการทดสอบ — ค่าเริ่มต้นคือ `apiFetch` ตัวเดียวกับ client อื่นทุกตัว */
  readonly fetchImpl?: typeof fetch;
  readonly signal?: AbortSignal;
}

export type DownloadFileResult =
  | { readonly status: 'ready' }
  | { readonly status: 'failed'; readonly response: Response }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'cancelled' };

/**
 * โหลดไฟล์ผ่าน `fetch` ที่ยืนยันตัวตนแล้ว แล้วสั่งให้เบราว์เซอร์เซฟเป็น `filename`
 *
 * **ไม่ใช่ `<a href="...">` ดิบ** — ดูเหตุผลที่ docstring ของไฟล์นี้ ความล้มเหลวระดับ
 * HTTP (`!response.ok`) คืนเป็น `'failed'` พร้อม `response` ดิบให้ผู้เรียกไปแปลเป็น
 * ความล้มเหลวของ contract นั้น ๆ เอง — ฟังก์ชันนี้ไม่รู้จักกฎธุรกิจของใครเลย
 *
 * object URL ถูกสร้างและ `revokeObjectURL` **ในฟังก์ชันเดียวกัน**: ไม่มีทางเรียก
 * ฟังก์ชันนี้แล้วลืม revoke เพราะผู้เรียกไม่มีบล็อบให้ถือไว้เอง
 */
export async function downloadFile(
  url: string,
  filename: string,
  options: DownloadFileOptions = {},
): Promise<DownloadFileResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  let response: Response;
  try {
    response = await fetchImpl(url, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }
  if (!response.ok) {
    return { status: 'failed', response };
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
  return { status: 'ready' };
}
